import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiActivity, FiClipboard, FiRepeat, FiTrendingUp, FiZap, FiArrowRight } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { getWebhookAnalyticsReport, type WebhookAnalyticsReport, type WebhookCallItem } from '../../services/webhookAnalyticsService';
import '../../styles/DwAgentDashboardSidebar.css';

type CallTab = 'all' | 'commands' | 'routines';

interface CallItem {
    type: 'command' | 'routine';
    title: string;
    taskNumber: string | null;
    status: 'done' | 'failed' | 'running';
    time: string;
}

const STATUS_COLOR: Record<string, string> = { done: '#22c55e', failed: '#ef4444', running: '#f59e0b' };
const STATUS_LABEL: Record<string, string> = { done: 'Done', failed: 'Failed', running: 'Running' };

/** "2h ago" style relative label for a webhook call's created_at timestamp. */
function relativeTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (diffSec < 60) return 'just now';
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    return `${diffDay}d ago`;
}

function toCallItem(c: WebhookCallItem): CallItem {
    return {
        type: c.type === 'ROUTINE' ? 'routine' : 'command',
        title: c.title || c.taskNumber || (c.type === 'ROUTINE' ? 'Routine run' : 'Command'),
        taskNumber: c.taskNumber,
        status: c.status === 'DONE' ? 'done' : c.status === 'FAILED' ? 'failed' : 'running',
        time: relativeTime(c.createdAt),
    };
}

const CallRow: React.FC<{ item: CallItem }> = ({ item }) => {
    const typeColor = item.type === 'command' ? '#6366f1' : '#00b0d3';
    return (
        <div className="dwa-sb-activity__item">
            <div className="dwa-sb-activity__icon-wrap" style={{ background: `${typeColor}18`, color: typeColor }}>
                {item.type === 'command' ? <FiClipboard size={11} /> : <FiRepeat size={11} />}
            </div>
            <div className="dwa-sb-activity__text">
                <div className="dwa-sb-activity__title">
                    <span className="dwa-sb-activity__title-text">{item.title}</span>
                </div>
                <div className="dwa-sb-activity__meta">
                    <span className="dwa-sb-activity__status" style={{ color: STATUS_COLOR[item.status] }}>{STATUS_LABEL[item.status]}</span>
                    {item.type === 'command' && item.taskNumber && item.taskNumber !== item.title && (
                        <span className="dwa-sb-activity__time">{item.taskNumber}</span>
                    )}
                    <span className="dwa-sb-activity__time">{item.time}</span>
                </div>
            </div>
        </div>
    );
};

const WebhookAnalyticsSidebarContent: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<CallTab>('all');
    const [report, setReport] = useState<WebhookAnalyticsReport | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        getWebhookAnalyticsReport()
            .then((r) => { if (!cancelled) setReport(r); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const totalCalls = report?.summary.totalCount ?? 0;
    const commandCalls = report?.summary.commandsCreated ?? 0;
    const routineCalls = report?.summary.routinesTriggered ?? 0;
    const successRate = report?.summary.successRate ?? 0;

    const stats = [
        {
            icon: <FiActivity size={15} />, iconBg: 'rgba(244,63,94,0.12)', iconColor: '#f43f5e',
            val: totalCalls, lbl: 'Total Calls',
            footer: <span className="dwa-sb-footer-note--purple">All time</span>,
        },
        {
            icon: <FiClipboard size={15} />, iconBg: 'rgba(99,102,241,0.12)', iconColor: '#6366f1',
            val: commandCalls, lbl: 'Commands Created',
            footer: <span className="dwa-sb-footer-note--indigo">{totalCalls > 0 ? Math.round((commandCalls / totalCalls) * 100) : 0}% of calls</span>,
        },
        {
            icon: <FiRepeat size={15} />, iconBg: 'rgba(0,176,211,0.12)', iconColor: '#00b0d3',
            val: routineCalls, lbl: 'Routines Triggered',
            footer: <span className="dwa-sb-footer-note--indigo">{totalCalls > 0 ? Math.round((routineCalls / totalCalls) * 100) : 0}% of calls</span>,
        },
        {
            icon: <FiTrendingUp size={15} />, iconBg: 'rgba(34,197,94,0.12)', iconColor: '#22c55e',
            val: `${successRate}%`, lbl: 'Success Rate',
            footer: <span className="dwa-sb-footer-note--red">{totalCalls} total</span>,
        },
    ];

    const recentAll = (report?.all || []).map(toCallItem);
    const recentCommands = (report?.commands || []).map(toCallItem);
    const recentRoutines = (report?.routines || []).map(toCallItem);

    const tabs: { id: CallTab; label: string; icon: React.ReactNode; count: number; color: string }[] = [
        { id: 'all', label: 'All', icon: <FiZap size={11} />, count: recentAll.length, color: '#f43f5e' },
        { id: 'commands', label: 'Commands', icon: <FiClipboard size={11} />, count: recentCommands.length, color: '#6366f1' },
        { id: 'routines', label: 'Routines', icon: <FiRepeat size={11} />, count: recentRoutines.length, color: '#00b0d3' },
    ];

    const activeItems = activeTab === 'commands' ? recentCommands : activeTab === 'routines' ? recentRoutines : recentAll;

    return (
        <div className="dwa-sb-wrap">
            <div className="dwa-sb-body">

                <div className="dwa-sb-stats">
                    {stats.map((s, i) => (
                        <div className="dwa-sb-stat-card" key={i}>
                            <div className="dwa-sb-stat-card__top">
                                <div className="dwa-sb-stat-card__icon" style={{ background: s.iconBg, color: s.iconColor }}>
                                    {s.icon}
                                </div>
                                <div className="dwa-sb-stat-card__body">
                                    <span className="dwa-sb-stat-card__val">{s.val}</span>
                                    <span className="dwa-sb-stat-card__lbl">{s.lbl}</span>
                                </div>
                            </div>
                            <div className="dwa-sb-stat-card__footer">{s.footer}</div>
                        </div>
                    ))}
                </div>

                <div className="dwa-sb-activity">
                    <div className="dwa-sb-section-lbl">Recent Calls</div>

                    <div className="dwa-sb-tabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`dwa-sb-tab${activeTab === tab.id ? ' dwa-sb-tab--active' : ''}`}
                                style={activeTab === tab.id ? { color: tab.color, borderBottomColor: tab.color } : {}}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <span className="dwa-sb-tab__icon" style={activeTab === tab.id ? { color: tab.color } : {}}>
                                    {tab.icon}
                                </span>
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="dwa-sb-tab-content">
                        {loading ? (
                            <div className="dwa-sb-empty">Loading…</div>
                        ) : activeItems.length === 0 ? (
                            <div className="dwa-sb-empty">No {activeTab === 'all' ? 'calls' : activeTab} yet</div>
                        ) : (
                            activeItems.map((item, i) => <CallRow key={i} item={item} />)
                        )}
                    </div>

                    <button className="dwa-sb-view-all-btn" onClick={() => navigate(`${ROUTES.SETTINGS_WEBHOOKS_REPORT}?tab=${activeTab}`)}>
                        View more
                        <FiArrowRight size={11} />
                    </button>
                </div>

            </div>
        </div>
    );
};

export default WebhookAnalyticsSidebarContent;
